import { TextField } from '@flos/react-ui';
import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import { useRecoilState, useRecoilValue } from 'recoil';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { useState } from 'react';
import { useHippoText } from '../../../../hooks/useHippoText';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import { type CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import _ from 'lodash';
import ViewHippoInformationModal from '../../../../shared_components/ViewHippoInformationModal/ViewHippoInformationModal';
import { isRequestPendingAtom } from '../../../../recoil/atoms/isRequestPendingAtom';
import { track } from '../../../../utils/trackingUtil';
import { shouldPrefillNameOfInsuredAtom } from '../../../../recoil/atoms/shouldPrefillNameOfInsuredAtom';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import HelpLink from '../../../../shared_components/HelpLink/HelpLink';

const NameOfTheInsured = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const [showModal, setShowModal] = useState(false);
    const isRequestPending = useRecoilValue(isRequestPendingAtom);
    const [coverables, setCoverables] = useRecoilState(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const accidentCoverables =
        currentProduct.flavourName === 'Ulykkesforsikring'
            ? coverables.adultAccidents ?? []
            : coverables.youngAccidents ?? [];
    const coverableIndex = accidentCoverables?.findIndex(
        (coverable) => coverable.fixedId === currentProduct.fixedId
    );
    const [shouldPrefillNameOfInsured, setShouldPrefillNameOfInsured] = useRecoilState(
        shouldPrefillNameOfInsuredAtom
    );
    const getInsuredName = (): { firstName: string; lastName: string } => {
        if (!shouldPrefillNameOfInsured) return { firstName: '', lastName: '' };
        return {
            firstName: accidentCoverables[coverableIndex].firstName ?? '',
            lastName: accidentCoverables[coverableIndex].lastName ?? ''
        };
    };

    const [firstName, setFirstName] = useState<string>(getInsuredName().firstName);
    const [lastName, setLastName] = useState<string>(getInsuredName().lastName);
    const [isLoading, setIsLoading] = useState(false);

    const viewInformationModalContent = {
        title: 'DSS-COVERABLE-INFORMATION-STEP-ORDER-FOR-EXPLANATION-MODAL-HEADER',
        description: 'DSS-COVERABLE-INFORMATION-STEP-ORDER-FOR-EXPLANATION-MODAL-SUBHEADER'
    };

    const isValid = !_.isEmpty(firstName) && !_.isEmpty(lastName);

    const handleNameOfInsuredBlur = (): void => {
        if (
            !accidentCoverables ||
            coverableIndex === undefined ||
            coverableIndex === -1 ||
            !firstName ||
            !lastName
        ) {
            return;
        }
        const updatedCoverables = [...accidentCoverables];

        updatedCoverables[coverableIndex] = {
            ...updatedCoverables[coverableIndex],
            firstName,
            lastName,
            name: firstName
        };

        setCoverables((prev) => ({
            ...prev,
            [currentProduct.flavourName === 'Ulykkesforsikring'
                ? 'adultAccidents'
                : 'youngAccidents']: updatedCoverables
        }));
    };

    const onNext = (): void => {
        setIsLoading(true);
        updateCoverableInformation({ name: firstName });
        const updatedCoverable: CoverableInformationToUpdateDTO = {
            [currentProduct.flavourName === 'Ulykkesforsikring'
                ? 'adultAccident'
                : 'youngAccident']: {
                publicId: accidentCoverables[coverableIndex].publicId,
                fixedId: accidentCoverables[coverableIndex].fixedId,
                firstName: accidentCoverables[coverableIndex].firstName,
                lastName: accidentCoverables[coverableIndex].lastName
            },
            productToUpdate: currentProduct.flavourName
        };

        void saveCoverableInformation(updatedCoverable).then(() => {
            const accidentType =
                currentProduct.flavourName === 'Ulykkesforsikring'
                    ? 'adultAccident'
                    : 'youngAccident';
            track(`${accidentType}CoverableInformationNameOfInsured`, [
                {
                    firstName: accidentCoverables[coverableIndex].firstName
                },
                {
                    lastName: accidentCoverables[coverableIndex].lastName
                }
            ]);
            goNext();
            setShouldPrefillNameOfInsured(true);
            setIsLoading(false);
        });
    };

    return (
        <WizardPagetemplate
            onNext={onNext}
            onPrevious={goPrevious}
            disableNext={!isValid || isLoading || isRequestPending}
            isNextLoading={isLoading || isRequestPending}
            disablePrevious={isLoading || isRequestPending}
        >
            <CoverableDetailsTemplate
                header={getHippoText('DSS-COVERABLE-INFORMATION-STEP-NAME-OF-INSURED-HEADER')}
            >
                <div className="u-display-flex u-flex-items-stretch u-flex-items-start u-gap-3 u-flex-row@xs u-flex-column">
                    <TextField
                        data-testid="adultAccNameId"
                        label={getHippoText(
                            'DSS-COVERABLE-INFORMATION-STEP-FIRST-NAME-OF-INSURED-INPUTFIELD'
                        )}
                        value={firstName}
                        onChange={(event) => {
                            setFirstName(event.target.value);
                        }}
                        onBlur={handleNameOfInsuredBlur}
                        minLength={1}
                        required
                        disabled={isLoading || isRequestPending}
                    />
                    <TextField
                        data-testid="adultAccNameId"
                        label={getHippoText(
                            'DSS-COVERABLE-INFORMATION-STEP-LAST-NAME-OF-INSURED-INPUTFIELD'
                        )}
                        value={lastName}
                        onBlur={handleNameOfInsuredBlur}
                        onChange={(event) => {
                            setLastName(event.target.value);
                        }}
                        minLength={1}
                        required
                        disabled={isLoading || isRequestPending}
                    />
                </div>
                <HelpLink
                    onClick={() => {
                        setShowModal(true);
                    }}
                >
                    {getHippoText('DSS-COVERABLE-INFORMATION-STEP-NAME-OF-INSURED-LINK')}
                </HelpLink>
            </CoverableDetailsTemplate>
            {showModal && (
                <ViewHippoInformationModal
                    showModal={showModal}
                    modalProps={viewInformationModalContent}
                    closeModal={() => {
                        setShowModal(false);
                    }}
                />
            )}
        </WizardPagetemplate>
    );
};

export default NameOfTheInsured;
